import { useEffect, useState } from "react";
import { formatBytes } from "../lib/format";

const MAX_PREVIEW_ROWS = 200;

interface ParsedCsv {
  header: string[];
  rows: string[][];
  totalRows: number; // total data rows in the file (including ones we truncated)
  totalBytes: number;
}

/**
 * Preview a CSV from a presigned S3 URL without making the user download
 * the file. Reused by Run Detail (input + fix CSV) and Upload Detail
 * (submitted CSV). Renders the header as <th> and the first N data rows
 * as <td>; everything past that gets a "and X more rows" hint so the user
 * knows the file is bigger than what's on screen.
 *
 * The CSV format is the PeopleSoft enrollment schema —
 *   "course_id","user_id","role","section_id","status"
 * — so a regex-based quoted-field parser is sufficient. Won't handle
 * escaped quotes or embedded commas, but those don't appear in real data.
 */
export function CsvPreviewModal({
  title,
  url,
  onClose,
  onDownload,
}: {
  title: string;
  url: string | null;
  onClose: () => void;
  onDownload?: () => void;
}) {
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setParsed(null);
    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const text = await r.text();
        if (cancelled) return;
        setParsed(parseCsv(text));
      })
      .catch((e: unknown) => {
        if (!cancelled) setError((e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal csv-preview-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="csv-preview-title"
      >
        <div className="csv-preview-head">
          <div>
            <h2 className="modal-title" id="csv-preview-title">
              {title}
            </h2>
            {parsed && (
              <div className="muted small csv-preview-stats">
                {parsed.totalRows.toLocaleString()} row
                {parsed.totalRows === 1 ? "" : "s"}
                {" · "}
                {formatBytes(parsed.totalBytes)}
                {parsed.totalRows > MAX_PREVIEW_ROWS && (
                  <> · showing first {MAX_PREVIEW_ROWS}</>
                )}
              </div>
            )}
          </div>
          <div className="csv-preview-head-actions">
            {onDownload && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={onDownload}
                title="Download the full file"
              >
                Download
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              onClick={onClose}
              aria-label="Close preview"
            >
              ×
            </button>
          </div>
        </div>

        <div className="csv-preview-body">
          {loading && <div className="muted">Loading preview…</div>}
          {error && (
            <div className="error">
              Couldn't load preview: {error}
              {onDownload && <> — try the download button instead.</>}
            </div>
          )}
          {parsed && !error && (
            <table className="csv-preview-table">
              <thead>
                <tr>
                  {parsed.header.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {parsed && parsed.totalRows > MAX_PREVIEW_ROWS && (
            <div className="muted small csv-preview-truncated">
              …and {(parsed.totalRows - MAX_PREVIEW_ROWS).toLocaleString()} more
              row{parsed.totalRows - MAX_PREVIEW_ROWS === 1 ? "" : "s"}.
              Download the file to see everything.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function parseCsv(text: string): ParsedCsv {
  const totalBytes = new Blob([text]).size;
  const allLines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (allLines.length === 0) {
    return { header: [], rows: [], totalRows: 0, totalBytes };
  }
  const header = splitQuoted(allLines[0]!);
  const dataLines = allLines.slice(1);
  const previewLines = dataLines.slice(0, MAX_PREVIEW_ROWS);
  const rows = previewLines.map(splitQuoted);
  return { header, rows, totalRows: dataLines.length, totalBytes };
}

function splitQuoted(line: string): string[] {
  // Pull out every "..." segment. Falls back to comma-split if the line
  // doesn't look like the quoted format (which doesn't happen in practice
  // for our PeopleSoft / Canvas SIS CSVs).
  const matches = line.match(/"([^"]*)"/g);
  if (matches) return matches.map((m) => m.slice(1, -1));
  return line.split(",");
}
